import React from 'react';
import { AlertTriangle, CheckCircle2, Plus } from 'lucide-react';
import type { Cluster, Rolle, Schutzklasse, Situation, Stufung } from '../types';
import { pruefeStufung } from '../schutzklassen';
import {
    AuswahlFeld, HakenFeld, Leerzeile, MarkenFeld, NeuZeile, TextFeld, Untertitel, ZeilenKarte,
} from './bausteine';

/**
 * Rollen mit ihren Situationen — und die Auf-/Abstufungen.
 *
 * ═══ WARUM DIE SITUATION UND NICHT DIE ROLLE DIE KLASSE TRAEGT ════════════
 * Ein Baggerfahrer traegt in derselben Schicht drei verschiedene Klassen: in
 * Cluster A/B Basisschutz in der beluefteten Kabine, in Cluster C dort SK2 —
 * und sobald er aussteigt, SK3. Wer die Klasse an die Rolle haengt, muss sich
 * fuer eine davon entscheiden, und das ist entweder zu viel Schutz (dann wird
 * er nicht getragen) oder zu wenig (dann fehlt er, wo er noetig waere).
 *
 * Deshalb: je Zeile eine Rolle in einem oder mehreren Bereichen, und darin
 * beliebig viele Situationen mit eigenem Ort und eigener Klasse.
 */

interface RollenProps {
    rollen: Rolle[];
    cluster: Cluster[];
    schutzklassen: Schutzklasse[];
    gesperrt: boolean;
    anlegen: (rolle: string) => Promise<unknown>;
    aendern: (id: string, u: Partial<Rolle>) => void;
    loeschen: (id: string) => void;
}

export const RollenEditor: React.FC<RollenProps> = ({
    rollen, cluster, schutzklassen, gesperrt, anlegen, aendern, loeschen,
}) => {
    const klassenOptionen = [...schutzklassen]
        .sort((a, b) => a.rang - b.rang)
        .map(k => ({ wert: k.key, label: `${k.key} — ${k.bezeichnung}` }));
    const kennungen = cluster.map(c => c.kennung);

    return (
        <>
            <NeuZeile
                platzhalter="Rolle, z. B. Baggerfahrer"
                knopf="Rolle"
                gesperrt={gesperrt}
                onAnlegen={anlegen}
            />

            {rollen.length === 0 ? (
                <Leerzeile text="Noch keine Rolle beschrieben." />
            ) : (
                <ul className="space-y-3">
                    {rollen.map(r => (
                        <ZeilenKarte
                            key={r.id}
                            gesperrt={gesperrt}
                            onLoeschen={() => loeschen(r.id)}
                            loeschTitel={`${r.rolle} löschen`}
                            kopf={
                                <span className="font-bold text-slate-800">
                                    {r.rolle}
                                    <span className="ml-2 text-xs font-normal text-slate-500">
                                        {r.cluster.length > 0 ? `Bereiche ${r.cluster.join(', ')}` : 'ohne Bereich'}
                                        {' · '}{r.situationen.length} Situation(en)
                                    </span>
                                </span>
                            }
                        >
                            <div className="grid sm:grid-cols-2 gap-2 mb-3">
                                <TextFeld
                                    label="Rolle"
                                    wert={r.rolle}
                                    gesperrt={gesperrt}
                                    onSpeichern={v => aendern(r.id, { rolle: v })}
                                />
                                <MarkenFeld
                                    label="Gilt in den Bereichen"
                                    werte={r.cluster}
                                    vorschlaege={kennungen}
                                    gesperrt={gesperrt}
                                    platzhalter="Kennung"
                                    onSpeichern={v => aendern(r.id, { cluster: v })}
                                />
                            </div>

                            {/* ── Arbeitsschritte ───────────────────────── */}
                            <Untertitel
                                text="Arbeitsschritte in Reihenfolge"
                                hinweis="Was diese Rolle in diesem Bereich tut — von der Schicht bis zum Verlassen."
                            />
                            <ol className="space-y-1.5 mb-2">
                                {r.schritte.map((s, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                        <span className="w-6 shrink-0 text-xs font-black text-slate-400">
                                            {s.nr}.
                                        </span>
                                        <span className="flex-1">
                                            <TextFeld
                                                wert={s.text}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => aendern(r.id, {
                                                    schritte: r.schritte.map((x, j) =>
                                                        (j === i ? { ...x, text: v } : x)),
                                                })}
                                            />
                                        </span>
                                        {!gesperrt && (
                                            <button
                                                type="button"
                                                onClick={() => aendern(r.id, {
                                                    schritte: r.schritte
                                                        .filter((_, j) => j !== i)
                                                        .map((x, j) => ({ ...x, nr: j + 1 })),
                                                })}
                                                className="text-xs text-slate-400 hover:text-red-600"
                                            >
                                                entfernen
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ol>
                            {!gesperrt && (
                                <button
                                    type="button"
                                    onClick={() => aendern(r.id, {
                                        schritte: [...r.schritte, { nr: r.schritte.length + 1, text: '' }],
                                    })}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-stone-700 underline mb-3"
                                >
                                    <Plus className="w-3 h-3" /> Schritt
                                </button>
                            )}

                            {/* ── Situationen ───────────────────────────── */}
                            <Untertitel
                                text="Schutzklasse je Situation"
                                hinweis="In der Kabine gilt etwas anderes als außerhalb — deshalb je Ort eine eigene Zeile."
                            />
                            <ul className="space-y-1.5 mb-2">
                                {r.situationen.map((s, i) => (
                                    <SituationsZeile
                                        key={i}
                                        situation={s}
                                        klassenOptionen={klassenOptionen}
                                        gesperrt={gesperrt}
                                        onAendern={u => aendern(r.id, {
                                            situationen: r.situationen.map((x, j) =>
                                                (j === i ? { ...x, ...u } : x)),
                                        })}
                                        onEntfernen={() => aendern(r.id, {
                                            situationen: r.situationen.filter((_, j) => j !== i),
                                        })}
                                    />
                                ))}
                            </ul>
                            {r.situationen.length === 0 && (
                                <p className="text-xs text-amber-700 mb-2">
                                    Ohne Situation trägt diese Rolle keine Schutzklasse.
                                </p>
                            )}
                            {!gesperrt && (
                                <button
                                    type="button"
                                    onClick={() => aendern(r.id, {
                                        situationen: [...r.situationen, {
                                            ort: '', schutzklasse_key: '', bereitstellung: false, hinweis: '',
                                        }],
                                    })}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-stone-700 underline"
                                >
                                    <Plus className="w-3 h-3" /> Situation
                                </button>
                            )}
                        </ZeilenKarte>
                    ))}
                </ul>
            )}
        </>
    );
};

const SituationsZeile: React.FC<{
    situation: Situation;
    klassenOptionen: { wert: string; label: string }[];
    gesperrt: boolean;
    onAendern: (u: Partial<Situation>) => void;
    onEntfernen: () => void;
}> = ({ situation: s, klassenOptionen, gesperrt, onAendern, onEntfernen }) => (
    <li className="grid sm:grid-cols-[1.4fr_1fr_auto_auto] gap-2 items-center">
        <TextFeld
            wert={s.ort}
            platzhalter="in der Kabine / außerhalb / im Schwarzbereich"
            gesperrt={gesperrt}
            onSpeichern={v => onAendern({ ort: v })}
        />
        <AuswahlFeld
            wert={s.schutzklasse_key}
            optionen={klassenOptionen}
            gesperrt={gesperrt}
            onSpeichern={v => onAendern({ schutzklasse_key: v })}
        />
        <HakenFeld
            label="auf Bereitstellung"
            wert={!!s.bereitstellung}
            gesperrt={gesperrt}
            onSpeichern={v => onAendern({ bereitstellung: v })}
        />
        {!gesperrt && (
            <button
                type="button"
                onClick={onEntfernen}
                className="text-xs text-slate-400 hover:text-red-600"
            >
                entfernen
            </button>
        )}
    </li>
);

// ═══════════════════════════════════════════════════════════════════════════
// AUF- UND ABSTUFUNGEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Stufungen als Vorgang.
 *
 * Die Asymmetrie ist der Punkt: AUFstufen darf der Aufsichtsfuehrende sofort
 * und allein, ABstufen nur der Koordinator, schriftlich und auf Nachweis.
 * Deshalb zeigt jede Zeile, was ihr zur Wirksamkeit noch fehlt — eine
 * unvollstaendige Abstufung GILT NICHT, auch wenn sie erfasst ist.
 */
export const StufungenEditor: React.FC<{
    stufungen: Stufung[];
    cluster: Cluster[];
    schutzklassen: Schutzklasse[];
    gesperrt: boolean;
    anlegen: (kennung: string, richtung: 'auf' | 'ab', von: string, nach: string) => Promise<unknown>;
    aendern: (id: string, u: Partial<Stufung>) => void;
}> = ({ stufungen, cluster, schutzklassen, gesperrt, anlegen, aendern }) => {
    const klassenOptionen = [...schutzklassen]
        .sort((a, b) => a.rang - b.rang)
        .map(k => ({ wert: k.key, label: k.key }));

    return (
        <>
            {!gesperrt && cluster.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {cluster.filter(c => c.art !== 'abgegrenzt').map(c => (
                        <React.Fragment key={c.id}>
                            <button
                                type="button"
                                onClick={() => void anlegen(c.kennung, 'auf', c.schutzklasse_key, '')}
                                className="px-2.5 py-1.5 border border-amber-300 bg-amber-50 rounded-lg
                                           text-xs font-bold text-amber-900"
                            >
                                ↑ Aufstufung in {c.kennung}
                            </button>
                            <button
                                type="button"
                                onClick={() => void anlegen(c.kennung, 'ab', c.schutzklasse_key, '')}
                                className="px-2.5 py-1.5 border border-slate-300 rounded-lg
                                           text-xs font-bold text-slate-600"
                            >
                                ↓ Abstufung in {c.kennung}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}

            {stufungen.length === 0 ? (
                <Leerzeile text="Keine Auf- oder Abstufung erfasst." />
            ) : (
                <ul className="space-y-2">
                    {[...stufungen]
                        .sort((a, b) => (b.entschieden_am ?? '').localeCompare(a.entschieden_am ?? ''))
                        .map(s => {
                            const fehlt = pruefeStufung(s);
                            return (
                                <ZeilenKarte
                                    key={s.id}
                                    gesperrt
                                    kopf={
                                        <span className="flex items-center gap-2">
                                            {fehlt.length === 0
                                                ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                                : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                                            <span className="font-bold text-slate-800">
                                                {s.richtung === 'auf' ? 'Aufstufung' : 'Abstufung'} in {s.cluster_kennung}
                                            </span>
                                            <span className="text-xs text-slate-500">
                                                {s.von_key || '?'} → {s.nach_key || '?'}
                                                {s.entschieden_am && ` · ${s.entschieden_am}`}
                                            </span>
                                        </span>
                                    }
                                >
                                    <div className="grid sm:grid-cols-4 gap-2 mb-2">
                                        <AuswahlFeld
                                            label="von"
                                            wert={s.von_key}
                                            optionen={klassenOptionen}
                                            gesperrt={gesperrt}
                                            onSpeichern={v => aendern(s.id, { von_key: v })}
                                        />
                                        <AuswahlFeld
                                            label="nach"
                                            wert={s.nach_key}
                                            optionen={klassenOptionen}
                                            gesperrt={gesperrt}
                                            onSpeichern={v => aendern(s.id, { nach_key: v })}
                                        />
                                        <TextFeld
                                            label="Bereichshinweis"
                                            wert={s.bereich_hinweis}
                                            platzhalter="Feld 51"
                                            gesperrt={gesperrt}
                                            onSpeichern={v => aendern(s.id, { bereich_hinweis: v })}
                                        />
                                        <TextFeld
                                            label="Datum (JJJJ-MM-TT)"
                                            wert={s.entschieden_am ?? ''}
                                            gesperrt={gesperrt}
                                            onSpeichern={v => aendern(s.id, { entschieden_am: v || null })}
                                        />
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-2 mb-2">
                                        {s.richtung === 'auf' ? (
                                            <TextFeld
                                                label="Anlass"
                                                wert={s.anlass}
                                                platzhalter="Geruch beim Anfahren der Sohle"
                                                mehrzeilig
                                                gesperrt={gesperrt}
                                                onSpeichern={v => aendern(s.id, { anlass: v })}
                                            />
                                        ) : (
                                            <TextFeld
                                                label="Grundlage"
                                                wert={s.grundlage}
                                                platzhalter="Trennlage und Abdeckung eingebaut, Weißbereich hergestellt"
                                                mehrzeilig
                                                gesperrt={gesperrt}
                                                hinweis="Hergestellter Weißbereich oder Messreihe über mehrere Schichten."
                                                onSpeichern={v => aendern(s.id, { grundlage: v })}
                                            />
                                        )}
                                        <div className="space-y-2">
                                            <TextFeld
                                                label="Veranlasst von"
                                                wert={s.entschieden_von}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => aendern(s.id, { entschieden_von: v })}
                                            />
                                            {s.richtung === 'ab' && (
                                                <TextFeld
                                                    label="Bestätigt durch den Koordinator"
                                                    wert={s.bestaetigt_von}
                                                    gesperrt={gesperrt}
                                                    hinweis="Eine Abstufung ohne diese Bestätigung gilt nicht."
                                                    onSpeichern={v => aendern(s.id, { bestaetigt_von: v })}
                                                />
                                            )}
                                            <HakenFeld
                                                label="im Bautagebuch vermerkt"
                                                wert={s.bautagebuch}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => aendern(s.id, { bautagebuch: v })}
                                            />
                                        </div>
                                    </div>

                                    {fehlt.length > 0 && (
                                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2">
                                            <p className="text-[11px] font-bold text-amber-900 mb-0.5">
                                                Diese Stufung gilt noch nicht:
                                            </p>
                                            <ul className="space-y-0.5">
                                                {fehlt.map((f, i) => (
                                                    <li key={i} className="text-[11px] text-amber-900">• {f}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </ZeilenKarte>
                            );
                        })}
                </ul>
            )}
        </>
    );
};
